/**
 * SettingsPageShell (M286, settings audit idea 9): the one header of a settings page. A breadcrumb of real links
 * (Settings › any `crumbs` › this page), the title at one size, an optional description and an actions slot on the
 * right, then the page. `width` is the page's own container width; a denied page renders SettingsDenied, which sits
 * inside this shell so the breadcrumb still leads back. The /devtools pages that left the hub (idea 18) pass
 * `root={DEVTOOLS_CRUMB}`: Developer tools › this page.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

export type SettingsPageWidth = '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full';

/** A parent page between the hub and this one, e.g. Security & Permissions above the Dashboard designer. No `to`: plain text. */
export interface SettingsCrumb {
  label: string;
  to?: string;
}

const SETTINGS_CRUMB: SettingsCrumb = { label: 'Settings', to: '/settings' };
/** /devtools has no index page, so its crumb is not a link. */
export const DEVTOOLS_CRUMB: SettingsCrumb = { label: 'Developer tools' };

const WIDTH: Record<SettingsPageWidth, string> = {
  '2xl': 'max-w-2xl mx-auto',
  '3xl': 'max-w-3xl mx-auto',
  '4xl': 'max-w-4xl mx-auto',
  '5xl': 'max-w-5xl mx-auto',
  '6xl': 'max-w-6xl mx-auto',
  '7xl': 'max-w-7xl mx-auto',
  full: '',
};

interface SettingsPageShellProps {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  width: SettingsPageWidth;
  crumbs?: SettingsCrumb[];
  /** The first crumb: Settings, or DEVTOOLS_CRUMB. */
  root?: SettingsCrumb;
  children?: ReactNode;
}

export function SettingsPageShell({ title, description, actions, width, crumbs = [], root = SETTINGS_CRUMB, children }: SettingsPageShellProps) {
  return (
    <div className={`p-6 ${WIDTH[width]}`}>
      <nav aria-label="Breadcrumb" className="mb-3 text-sm text-gray-500">
        <ol className="flex flex-wrap items-center gap-1">
          {[root, ...crumbs].map((c) => (
            <li key={c.label} className="inline-flex items-center gap-1">
              {c.to ? (
                <Link to={c.to} className="hover:text-gray-900 hover:underline">
                  {c.label}
                </Link>
              ) : (
                c.label
              )}
              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            </li>
          ))}
          <li aria-current="page" className="text-gray-700">
            {title}
          </li>
        </ol>
      </nav>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          {description && <div className="mt-1 max-w-3xl text-sm text-gray-600">{description}</div>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}
